// src/components/intake/SourceChoices.jsx
//
// The three ways into the print intake, in the ticket's order. Shown twice: on
// the Create popup's Intake tab (dark ground) and as the first step of /intake
// (light ground). Choosing a card acts at once, as in the mock-up.

import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { tokens } from '../../theme';
import { SP, FONT } from './intakeSx';

export const SOURCES = [
  {
    id: 'revisionari',
    title: 'A book in ReVisionari',
    desc: "Choose one of the books you've written here.",
    Icon: MenuBookOutlinedIcon,
  },
  {
    id: 'upload',
    title: 'Upload finished files',
    desc: 'You have a manuscript and a cover from outside ReVisionari.',
    Icon: UploadFileOutlinedIcon,
  },
  {
    id: 'new',
    title: "Haven't started writing yet",
    desc: 'Start a new manuscript in ReVisionari first, then come back here to print.',
    Icon: EditNoteOutlinedIcon,
  },
];

export default function SourceChoices({ tone = 'light', onChoose }) {
  const dark = tone === 'dark';
  const c = dark
    ? {
        line: tokens.color.lineOnDark,
        hoverLine: tokens.color.goldFlat,
        ground: 'transparent',
        hoverGround: alpha(SP.white, 0.06),
        fg: SP.white,
        sub: tokens.color.onDark,
        icon: tokens.color.purpleRaised,
        arrow: SP.goldLight,
      }
    : {
        line: SP.lineSoft,
        hoverLine: SP.purpleSoft,
        ground: SP.white,
        hoverGround: SP.lavender,
        fg: SP.black,
        sub: SP.gray,
        icon: SP.purple,
        arrow: SP.purpleSoft,
      };

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }} data-testid="intake-sources">
      {SOURCES.map(({ id, title, desc, Icon }) => (
        <Box
          key={id}
          component="button"
          type="button"
          onClick={() => onChoose(id)}
          data-testid={`intake-source-${id}`}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.75,
            width: '100%',
            textAlign: 'left',
            p: '16px 18px',
            borderRadius: '7px',
            border: `1.5px solid ${c.line}`,
            background: c.ground,
            color: c.fg,
            font: 'inherit',
            cursor: 'pointer',
            transition: 'border-color .18s ease, background .18s ease',
            '&:hover': { borderColor: c.hoverLine, background: c.hoverGround },
            '&:focus-visible': { borderColor: c.hoverLine, outline: `2px solid ${c.hoverLine}`, outlineOffset: 2 },
            '&:hover .intake-source-arrow': { transform: 'translateX(3px)' },
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '6px',
              bgcolor: c.icon,
              color: SP.white,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: FONT.serif, fontWeight: 600, fontSize: 15.5, color: 'inherit' }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: 12.5, mt: '3px', color: c.sub }}>{desc}</Typography>
          </Box>
          <ArrowForwardIcon
            className="intake-source-arrow"
            fontSize="small"
            sx={{ ml: 'auto', alignSelf: 'center', color: c.arrow, transition: 'transform .18s ease', flexShrink: 0 }}
          />
        </Box>
      ))}
    </Box>
  );
}
